import { FaCheckCircle, FaCloudUploadAlt } from 'react-icons/fa'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { usePaymentMethods, useSubmitPayment } from '@/hooks/usePayment'
import { useServiceRequest } from '@/hooks/useRequests'
import { useState } from 'react'
import { uploadImageToGCS } from '@/utils/uploadImage'
import routes from '@/routes/routes'
import Button from '@/components/common/Button/Button'
import toast from 'react-hot-toast'
import './PaymentUpload.css'

const PaymentUpload = () => {
    const { requestId } = useParams()
    const location = useLocation()
    const state = location.state as { requestId?: number; amount?: number } | undefined
    const numericRequestId = requestId ? Number(requestId) : state?.requestId
    
    // Fetch request data to get the amount
    const { data: requestData, isLoading: requestLoading, isError: requestError } = useServiceRequest(numericRequestId || 0)
    
    // Get amount from request data or state (fallback)
    // Priority: total_price > proposed_price > estimated_price > state > 0
    // Convert to number in case it's a string (Django DecimalField returns strings)
    // IMPORTANT: Use the exact value from backend to match validation
    const getAmount = () => {
        // Try to get the exact value that backend expects (total_price or proposed_price)
        const value = requestData?.total_price || 
                     requestData?.proposed_price || 
                     requestData?.estimated_price || 
                     requestData?.amount || 
                     requestData?.price ||
                     state?.amount
        if (value === null || value === undefined) return 0
        // Convert to number but preserve precision
        const numValue = typeof value === 'string' ? parseFloat(value) : Number(value)
        return isNaN(numValue) ? 0 : numValue
    }
    
    const amount = getAmount()
    
    // Get the exact amount value for submission (backend expects exact match)
    const getExactAmount = () => {
        // Use the exact value from backend, prefer total_price or proposed_price
        const exactValue = requestData?.total_price || requestData?.proposed_price
        if (exactValue !== null && exactValue !== undefined) {
            return typeof exactValue === 'string' ? parseFloat(exactValue) : Number(exactValue)
        }
        return amount
    }
    
    const { data: methods, isLoading: methodsLoading, isError } = usePaymentMethods()
    const submit = useSubmitPayment()
    const navigate = useNavigate()

    const [selectedMethod, setSelectedMethod] = useState<string>('')
    const [receiptFile, setReceiptFile] = useState<File | null>(null)
    const [uploading, setUploading] = useState(false)

    const methodsList = methods?.payment_methods || (methods ? Object.entries(methods).map(([k, v]: [string, any]) => ({ id: k, name: v.name || k, ...v })) : [])

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setReceiptFile(e.target.files[0])
        }
    }

    const handleSubmit = async () => {
        if (!numericRequestId) {
            toast.error('ID de solicitud no válido')
            return
        }
        
        if (!selectedMethod) {
            toast.error('Selecciona un método de pago')
            return
        }
        
        if (!receiptFile) {
            toast.error('Sube un comprobante de pago')
            return
        }
        
        if (!amount || amount <= 0) {
            toast.error('El monto no es válido. Por favor, recarga la página.')
            return
        }
        
        setUploading(true)
        try {
            toast.loading('Subiendo comprobante...', { id: 'upload' })
            const receiptUrl = await uploadImageToGCS(receiptFile, 'payment-receipts')
            
            toast.loading('Enviando pago...', { id: 'upload' })
            const exactAmount = getExactAmount()
            await submit.mutateAsync({
                service_request: numericRequestId,
                amount: exactAmount,
                payment_method: selectedMethod,
                receipt_image_url: receiptUrl,
            })
            
            toast.success('Pago enviado correctamente. Será revisado por nuestro equipo.', { id: 'upload' })
            setTimeout(() => {
            navigate(routes.requests)
            }, 1500)
        } catch (e: any) {
            console.error('Payment upload error:', e)
            const errorMessage = e?.response?.data?.error || 
                                e?.response?.data?.detail || 
                                e?.message || 
                                'Error al enviar el pago. Intenta nuevamente.'
            toast.error(errorMessage, { id: 'upload' })
        } finally {
            setUploading(false)
        }
    }

    return (
        <div className="payment-upload-page">
            {/* Header */}
            <div className="payment-upload-header">
                <div className="payment-upload-header-content">
                    <h1 className="payment-upload-title">Pagar Servicio</h1>
                    <p className="payment-upload-subtitle">Sube tu comprobante de pago</p>
                </div>
            </div>
            
            <div className="payment-upload-container">
            {(methodsLoading || requestLoading) ? (
                <div>Loading...</div>
            ) : (isError || requestError) ? (
                <div className="payment-upload_error">
                    {isError ? 'Failed to load payment methods.' : 'Failed to load request data.'}
                </div>
            ) : !numericRequestId ? (
                <div className="payment-upload_error">No request ID provided.</div>
            ) : (
            <div className="payment-upload_form">
                <div className="payment-upload_amount_display">
                    <div className="payment-upload_amount_label">Monto a pagar</div>
                    <div className="payment-upload_amount_value">
                        {amount > 0 ? `S/ ${amount.toFixed(2)}` : 'Monto no disponible'}
                    </div>
                    {requestData && amount <= 0 && (
                        <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#EF4444' }}>
                            No se pudo obtener el monto. Por favor, recarga la página o contacta soporte.
                        </div>
                    )}
                </div>

                <div className="payment-upload_section">
                    <div className="payment-upload_section_title">Selecciona método de pago</div>
                    <div className="payment-upload_methods">
                        {methodsList.map((m: any) => (
                            <div
                                key={m.id}
                                className={`payment-upload_method ${selectedMethod === m.id ? 'is-selected' : ''}`}
                                onClick={() => setSelectedMethod(m.id)}
                            >
                                <div className="payment-upload_method_head">
                                    <span className="payment-upload_radio">{selectedMethod === m.id ? <FaCheckCircle /> : <span className="payment-upload_radio-empty" />}</span>
                                    <div className="payment-upload_method_name">{m.name}</div>
                                </div>
                                {selectedMethod === m.id && (m.account_number || m.cci || m.phone) && (
                                    <div className="payment-upload_method_details">
                                        {m.account_number && <div>Cuenta: {m.account_number}</div>}
                                        {m.cci && <div>CCI: {m.cci}</div>}
                                        {m.phone && <div>Teléfono: {m.phone}</div>}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {selectedMethod && (
                    <div className="payment-upload_section">
                        <div className="payment-upload_section_title">Sube tu comprobante</div>
                        <label className="payment-upload_upload">
                            <input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />
                            {receiptFile ? (
                                <div className="payment-upload_file_selected"><FaCheckCircle /> {receiptFile.name}</div>
                            ) : (
                                <>
                                    <FaCloudUploadAlt className="payment-upload_upload_icon" />
                                    <div>Toca para subir comprobante</div>
                                    <div className="payment-upload_upload_hint">JPG, PNG (máx. 5MB)</div>
                                </>
                            )}
                        </label>
                    </div>
                )}

                <div className="payment-upload_actions">
                    <Button variant="secondary" onClick={() => navigate(routes.requests)}>Cancelar</Button>
                    <Button 
                        variant="primary" 
                        onClick={handleSubmit} 
                        disabled={!selectedMethod || !receiptFile || uploading || !amount || amount <= 0}
                    >
                        {uploading ? 'Subiendo...' : 'Enviar Comprobante'}
                    </Button>
                </div>
            </div>
            )}
            </div>
        </div>
    )
}

export default PaymentUpload


